import { useCallback, useState } from 'react';

/**
 * Provides a hook to track a stack of items. It returns the top item plus functions
 * for pushing and popping.
 *
 *     const [topItem, push, pop, reset] = useStack([firstItem]);
 *
 */
export default function useStack<T>(initialState: T[] = []): {
  currentStep: number;
  currentTopItem: T | undefined;
  peekBehind: () => T | null;
  pop: () => T;
  popN: (n?: number) => T | null;
  push: (newItem: T) => void;
  reset: (stack: T[]) => void;
  stack: T[];
} {
  const [stack, setStack] = useState(initialState);

  /** Pushes a new item on to the stack. */
  function push(item: T) {
    setStack([...stack, item]);
  }

  /** Pops the last item from the stack. */
  function pop(): T {
    const newItem = stack[stack.length - 2];
    setStack([...stack.slice(0, stack.length - 1)]);

    return newItem;
  }

  /** Pops the last n items from the stack and returns the new top element. */
  function popN(n = 1): T | null {
    if (n < 1) {
      throw new Error('Number of items to pop must be at least 1');
    }

    if (n > stack.length) {
      throw new Error('Cannot pop more items than the current stack size');
    }

    setStack([...stack.slice(0, stack.length - n)]);

    return stack.length - n > 0 ? stack[stack.length - n - 1] : null;
  }

  /** Peeks at the second to last item. */
  function peekBehind(): T | null {
    if (stack.length <= 1) return null;

    return stack[stack.length - 2];
  }

  const reset = useCallback((stack: T[]) => {
    setStack(stack);
  }, []);

  const currentTopItem = stack[stack.length - 1];

  const currentStep =
    stack?.filter((item) => item['_modelApiKey'] !== 'panel_interstitial')?.length ?? stack.length;

  return {
    currentStep,
    currentTopItem,
    peekBehind,
    pop,
    popN,
    push,
    reset,
    stack,
  };
}
